import LegalDocument, { type LegalSection } from '@/components/LegalDocument';

/**
 * Terms & Conditions.
 *
 * The credits clause (section 5) is the load-bearing part: it is the contractual
 * statement that credits are promotional, non-transferable and never withdrawable,
 * which mirrors the Privacy Policy and the behaviour enforced in
 * `apps/api/src/billing`. If you change the credit model, change this section.
 */
const UPDATED = '4 October 2026';
const CONTACT_EMAIL = 'legal@aimedia.example';

const SECTIONS: LegalSection[] = [
  {
    id: 'acceptance',
    heading: '1. Acceptance of these terms',
    body: (
      <>
        <p>
          These Terms & Conditions ("Terms") govern your use of the Social Media Autopilot service, including
          the web app, the iOS and Android apps, and the API (together, the "Service"), operated
          by Social Media Autopilot ("we", "us", "our").
        </p>
        <p>
          By creating an account or using the Service you agree to these Terms, our{' '}
          <a href="/legal/privacy">Privacy Policy</a>, and any plan-specific terms we publish. If
          you do not agree, do not use the Service. If you use the Service on behalf of a
          company, you confirm you are authorised to bind that company, and "you" refers to it.
        </p>
        <p>You must be at least 18 years old and legally able to enter into this agreement.</p>
      </>
    ),
  },
  {
    id: 'the-service',
    heading: '2. The Service',
    body: (
      <>
        <p>
          The Service connects social media accounts, generates content using artificial
          intelligence, schedules and publishes posts, and reports performance analytics.
        </p>
        <p>Plans and limits are described on the billing page and may change with notice.</p>
      </>
    ),
  },
  {
    id: 'accounts',
    heading: '3. Accounts',
    body: (
      <>
        <p>You must provide accurate registration information and keep it current.</p>
        <ul>
          <li>You are responsible for all activity under your account and for keeping credentials confidential.</li>
          <li>You must notify us promptly of any unauthorised access.</li>
          <li>You must not share, resell or transfer an account, and you may have only the accounts your plan allows.</li>
          <li>You must be legally able to enter into this agreement and to the platform terms you publish through.</li>
          <li>One person or entity per paid account; shared logins for multiple people are not permitted.</li>
        </ul>
        <p>We may suspend or close accounts that breach these Terms (see section 11).</p>
      </>
    ),
  },
  {
    id: 'acceptable-use',
    heading: '4. Acceptable use',
    body: (
      <>
        <p>You agree not to use the Service to:</p>
        <ul>
          <li>publish spam, misleading content, or material that is unlawful, defamatory, hateful, or incites violence;</li>
          <li>impersonate others, misrepresent your identity, or use another person's content without rights;</li>
          <li>evade platform limits, rate limits, or detection mechanisms, or automate engagement;</li>
          <li>infringe copyright, trademark, publicity or other rights;</li>
          <li>upload malware, or interfere with the Service or other users' use of it;</li>
          <li>scrape, resell, benchmark or reverse engineer the Service, except where the law permits;</li>
          <li>operate the Service for any unlawful purpose, or in breach of export or sanctions rules.</li>
        </ul>
        <p>
          You are solely responsible for the content you publish. We do not pre-approve content and
          do not review it, except as required by law.
        </p>
      </>
    ),
  },
  {
    id: 'credits',
    heading: '5. Credits — not money, and not withdrawable',
    body: (
      <>
        <p>
          <strong>
            The Service is denominated in credits. Any payment you make is converted into credits.
          </strong>
        </p>
        <p>
          When you purchase a plan by UPI or card, the money is settled by the payment gateway and
          applied to your account as that plan's credits. The rupee amount you pay is the price of
          the Service; it is not a deposit, and it does not sit on account as cash.
        </p>
        <ul>
          <li>
            <strong>Credits are promotional.</strong> They are not money, not currency, not a
            stored balance and not a prepaid payment instrument, and have no cash value outside
            the Service.
          </li>
          <li>
            <strong>Credits cannot be withdrawn or cashed out.</strong> There is no mechanism to
            convert credits into money, and we have no obligation to do so on request, on
            termination, or in any other circumstance.
          </li>
          <li>
            <strong>Credits are not transferable.</strong> You cannot sell, gift, trade or
            otherwise transfer credits, and you may not resell or transfer your account.
          </li>
          <li>
            <strong>Credits do not accrue interest</strong> and, unless we publish a promotional
            expiry in advance, do not expire.
          </li>
          <li>
            <strong>Credits may be changed, expired or revoked</strong> to correct an error, to
            reverse a payment that is reversed or charged back, or where they were obtained
            through abuse of the referral programme, to the extent permitted by law. We will
            notify you of a material change affecting you before it takes effect.
          </li>
          <li>
            <strong>Unused credits are not refunded.</strong> If you cancel or stop using a plan,
            remaining credits stay on your account and are usable against future purchases, but
            they carry no monetary value.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'payments',
    heading: '6. Payments, pricing and refunds',
    body: (
      <>
        <p>
          Payments are processed by Razorpay and can be made over <strong>UPI</strong> or{' '}
          <strong>card</strong>. You authorise the charge and are responsible for the payment
          method you provide.
        </p>
        <ul>
          <li>Prices are in Indian Rupees and exclude applicable taxes unless stated otherwise.</li>
          <li>
            A purchase completes only when the payment is confirmed. We verify the confirmation
            with the gateway before granting credits or changing your plan.
          </li>
          <li>
            <strong>Refunds.</strong> Refunds, where granted, are issued to the original payment
            method by the gateway. A refund is not calculated from your credit balance — credits
            are not money and are never converted into a refund.
          </li>
          <li>
            <strong>Chargebacks.</strong> A chargeback may suspend your account and reverse
            related credits.
          </li>
          <li>Repeated failed payments or chargebacks may result in suspension of paid features.</li>
        </ul>
        <p>
          Statutory rights that cannot be excluded (such as consumer cancellation rights where they
          apply) are unaffected by this section.
        </p>
      </>
    ),
  },
  {
    id: 'referral-program',
    heading: '7. Referral programme',
    body: (
      <>
        <p>
          We offer a referral programme under which you receive a unique code and invite link.
          When a new account signs up with your code and completes its first successful payment,
          both accounts are granted credits.
        </p>
        <ul>
          <li>Rewards are issued as <strong>credits</strong> — never as money — and are subject to section 5.</li>
          <li>A referral is credited once, to the account that actually made the payment.</li>
          <li>You may refer others only in accordance with these Terms and any published rules.</li>
          <li>
            We may <strong>suspend or void a referral</strong>, and remove any credits awarded, if
            we reasonably believe it was obtained through self-referral, duplicate accounts, bulk
            or automated sign-ups, misrepresentation, or any other abuse or manipulation.
          </li>
          <li>Programme rewards and mechanics may be changed or withdrawn prospectively with notice.</li>
        </ul>
        <p>
          We verify referrals before granting rewards to prevent abuse, and our records are
          authoritative for programme purposes.
        </p>
      </>
    ),
  },
  {
    id: 'your-content',
    heading: '8. Your content and licence',
    body: (
      <>
        <p>You keep ownership of the content you upload or create.</p>
        <p>
          You grant us a limited licence to host, store, process and display your content solely
          to operate the Service for you — for example, to generate variations, render media, and
          publish to the accounts you connect.
        </p>
        <p>
          You confirm you have the rights needed for everything you submit, and that your use
          does not infringe third-party rights or violate the platforms' terms.
        </p>
        <p>
          <strong>AI output.</strong> Content generated by our AI models is provided "as is". It
          may be inaccurate, unsuitable or infringe rights, and you are responsible for reviewing
          and editing it before publication.
        </p>
      </>
    ),
  },
  {
    id: 'third-party-platforms',
    heading: '9. Third-party platforms',
    body: (
      <p>
        The Service publishes to and reads from third-party platforms under their own terms,
        policies and technical limits, which may change at any time. We do not control those
        platforms and are not responsible for their availability, moderation decisions, or
        changes to their APIs. You are responsible for complying with their terms, including any
        automation or commercial-use restrictions.
      </p>
    ),
  },
  {
    id: 'termination',
    heading: '10. Suspension and termination',
    body: (
      <>
        <p>You may stop using the Service and close your account at any time.</p>
        <p>We may suspend or terminate an account for breach of these Terms, for non-payment, or where required by law.</p>
        <p>
          On termination, paid access continues to the end of the paid period, and any remaining
          credits stay on the account but have no monetary value — <strong>no payment, refund or
          withdrawal is owed</strong> (section 5). Content you published through the Service remains
          on third-party platforms.
        </p>
        <p>Sections that by their nature should survive termination do so.</p>
      </>
    ),
  },
  {
    id: 'disclaimers',
    heading: '11. Disclaimers',
    body: (
      <>
        <p>
          Except as expressly stated, the Service is provided "as is" and "as available". To the
          fullest extent permitted by law we disclaim implied warranties of merchantability,
          fitness for a particular purpose and non-infringement.
        </p>
        <p>
          We do not warrant that the Service will be uninterrupted or error-free, or that content
          will achieve any particular result on any platform. You are responsible for reviewing
          AI-generated content and for compliance with applicable advertising, consumer and
          platform rules.
        </p>
      </>
    ),
  },
  {
    id: 'liability',
    heading: '12. Limitation of liability',
    body: (
      <>
        <p>
          To the fullest extent permitted by law, we are not liable for indirect, incidental,
          special or consequential loss, or for loss of profits, revenue, data, followers or
          goodwill, arising from your use of the Service or a third party's act or omission.
        </p>
        <p>
          Our total aggregate liability to you for all claims relating to the Service is limited
          to the amount you paid us in the twelve months preceding the claim. Nothing here excludes
          liability for death or personal injury caused by negligence, for fraud, or for anything
          else that cannot lawfully be excluded.
        </p>
      </>
    ),
  },
  {
    id: 'indemnity',
    heading: '13. Indemnity',
    body: (
      <p>
        You agree to indemnify and hold us harmless from any claim or demand, including reasonable
        legal fees, arising from your content, your use of the Service in breach of these Terms, or
        your violation of any third-party right or law.
      </p>
    ),
  },
  {
    id: 'changes',
    heading: '14. Changes to these Terms',
    body: (
      <>
        <p>
          We may update these Terms. The "last updated" date at the top reflects the current
          version. For material changes we will provide notice in the app or by email before they
          take effect.
        </p>
        <p>Continuing to use the Service after a change means you accept the updated Terms.</p>
      </>
    ),
  },
  {
    id: 'general',
    heading: '15. General',
    body: (
      <>
        <p>
          These Terms are governed by the laws of India, and the courts at the place of our
          registered office have exclusive jurisdiction, without prejudice to any mandatory consumer
          protections available to you where you live.
        </p>
        <p>
          If any provision is found unenforceable, the rest remains in force. Our failure to
          enforce a right is not a waiver of it. You may not assign these Terms; we may assign them
          to an affiliate or successor. Headings are for convenience only.
        </p>
      </>
    ),
  },
  {
    id: 'contact',
    heading: '16. Contact',
    body: (
      <p>
        Questions about these Terms can be sent to{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. See also our{' '}
        <a href="/legal/privacy">Privacy Policy</a>.
      </p>
    ),
  },
];

export default function TermsPage() {
  return (
    <LegalDocument
      title="Terms & Conditions"
      updated={UPDATED}
      intro="These Terms & Conditions govern your use of the Social Media Autopilot service. Please read them carefully — in particular section 5, which explains that the Service is denominated in credits and that credits are not money and cannot be withdrawn."
      sections={SECTIONS}
    />
  );
}